import {
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AccountCircle,
  Edit,
  Email,
  Home,
  Lock,
  Phone,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { getProfile, updatePassword } from "apis";
import PasswordHints from "components/PasswordHints";
import ProfileDialog from "components/ProfileDialog";
import { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { RootState } from "store";
import { fetchProfile, ProfileState } from "store/features/profile";
import { stringAvatar } from "utils";
import { toAssetUrl } from "utils/apiConfig";

export interface IProfile {
  firstName: string;
  lastName: string;
  email: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    country: string;
    postcode: string;
  };
  mobile: string;
  photo?: string | File;
  removePhoto?: boolean;
}

type PasswordFields = {
  current_password: string;
  password: string;
  confirm_password: string;
};

const DetailRow: React.FC<{
  icon: React.ReactElement;
  label: string;
  value: string;
}> = ({ icon, label, value }) => {
  const theme = useTheme();
  return (
    <Box display="flex" alignItems="flex-start" gap={1.5} py={1.5}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          color: "primary.main",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
        >
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  );
};

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loaded, item: profile } = useSelector<RootState, ProfileState>(
    (store) => store.profile,
  );

  const [editOpen, setEditOpen] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    getValues,
    formState: { errors },
  } = useForm<PasswordFields>();

  useEffect(() => {
    if (loaded) return;
    getProfile().then(({ data }) => dispatch(fetchProfile(data)));
  }, [dispatch, loaded]);

  const savePassword: SubmitHandler<PasswordFields> = ({
    password,
    current_password,
  }) => {
    setSavingPassword(true);
    updatePassword({ password, current_password })
      .then(() => {
        enqueueSnackbar(t("setting.password_updated"), { variant: "success" });
        reset();
      })
      .catch((err) => {
        enqueueSnackbar(
          err?.response?.data?.message || t("setting.password_error"),
          { variant: "error" },
        );
      })
      .finally(() => setSavingPassword(false));
  };

  if (!loaded || !profile) return null;

  const addressParts = [
    profile.address?.line1,
    profile.address?.line2,
    profile.address?.city,
    profile.address?.postcode,
    profile.address?.country,
  ].filter(Boolean);

  return (
    <>
      <Toolbar
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          gap: 1.5,
        }}
      >
        <AccountCircle sx={{ color: "primary.main", fontSize: 22 }} />
        <Typography variant="h6" fontWeight={600}>
          {t("profile.label")}
        </Typography>
      </Toolbar>

      <Container sx={{ py: 3, maxWidth: 600 }} maxWidth={false}>
        {/* Profile header */}
        <Card variant="outlined" sx={{ mb: 2, overflow: "hidden" }}>
          <Box
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              height: 72,
            }}
          />
          <CardContent sx={{ pt: 0, textAlign: "center" }}>
            <Avatar
              src={toAssetUrl(profile.photo as string | undefined)}
              {...(!profile.photo
                ? stringAvatar(`${profile.firstName} ${profile.lastName}`, {
                    width: 80,
                    height: 80,
                    fontSize: 28,
                    border: `3px solid ${theme.palette.background.paper}`,
                    mx: "auto",
                    mt: "-40px",
                    mb: 1,
                    boxShadow: 1,
                  })
                : {
                    sx: {
                      width: 80,
                      height: 80,
                      fontSize: 28,
                      border: `3px solid ${theme.palette.background.paper}`,
                      mx: "auto",
                      mt: "-40px",
                      mb: 1,
                      boxShadow: 1,
                    },
                  })}
            />
            <Typography variant="h6" fontWeight={700}>
              {profile.firstName} {profile.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {profile.email}
            </Typography>
          </CardContent>
        </Card>

        {/* Contact details */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              {t("labels.general_info")}
            </Typography>
            <DetailRow
              icon={<Email sx={{ fontSize: 18 }} />}
              label={t("inputs.email")}
              value={profile.email}
            />
            <DetailRow
              icon={<Phone sx={{ fontSize: 18 }} />}
              label={t("inputs.mobile")}
              value={profile.mobile}
            />
            <DetailRow
              icon={<Home sx={{ fontSize: 18 }} />}
              label={t("address")}
              value={addressParts.join(", ")}
            />
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(theme.palette.warning.main, 0.08),
                  color: "warning.main",
                }}
              >
                <Lock sx={{ fontSize: 18 }} />
              </Box>
              <Typography variant="subtitle2" fontWeight={700}>
                {t("inputs.changePassword")}
              </Typography>
            </Box>

            <form onSubmit={handleSubmit(savePassword)}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    type={showCurrentPw ? "text" : "password"}
                    fullWidth
                    size="small"
                    label={t("inputs.currentPassword")}
                    error={!!errors.current_password}
                    {...register("current_password", { required: true })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setShowCurrentPw(!showCurrentPw)}
                            edge="end"
                          >
                            {showCurrentPw ? (
                              <VisibilityOff sx={{ fontSize: 18 }} />
                            ) : (
                              <Visibility sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    type={showNewPw ? "text" : "password"}
                    fullWidth
                    size="small"
                    label={t("inputs.newPassword")}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    {...register("password", {
                      required: "Required",
                      minLength: { value: 6, message: "Min 6 characters" },
                    })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setShowNewPw(!showNewPw)}
                            edge="end"
                          >
                            {showNewPw ? (
                              <VisibilityOff sx={{ fontSize: 18 }} />
                            ) : (
                              <Visibility sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    type={showNewPw ? "text" : "password"}
                    fullWidth
                    size="small"
                    label={t("inputs.confirmPassword")}
                    error={!!errors.confirm_password}
                    helperText={errors.confirm_password?.message}
                    {...register("confirm_password", {
                      required: "Required",
                      validate: (v) =>
                        v === getValues("password") || "Passwords don't match",
                    })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <PasswordHints value={watch("password")} />
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="warning"
                  disabled={savingPassword}
                >
                  {savingPassword ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    t("inputs.changePassword")
                  )}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Container>

      <Fab
        variant="extended"
        color="primary"
        sx={{
          position: "fixed",
          right: { xs: 16, sm: 24 },
          bottom: { xs: 16, sm: 24 },
          pl: 2.5,
          pr: 3,
          gap: 1,
        }}
        onClick={() => setEditOpen(true)}
      >
        <Edit />
        {t("profile.edit")}
      </Fab>

      {editOpen && (
        <ProfileDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          item={profile}
        />
      )}
    </>
  );
};

export default Profile;
